/**
 * Money arrives with `micros` as a STRING, and it has to be read with
 * BigInt.
 *
 * A JSON number is a float64 to every JavaScript client, exact only to 2^53.
 * A balance in micros passes that on its own for a large account, and any sum
 * over a page of transactions passes it much sooner — silently, in the
 * browser, with no error to notice. So nothing in the portals converts micros
 * to a Number; formatting goes straight from BigInt to a string.
 */
/** Micros per MINOR unit (cent), matching internal/money.MicrosPerMinor. A
 *  price is routinely a fraction of a cent, which is why the platform's unit
 *  of account is a millionth of one. */
export const MICROS_PER_MINOR = 1000000n;
export function micros(amount) {
    return BigInt(amount.micros);
}
export function isZero(amount) {
    return micros(amount) === 0n;
}
export function isNegative(amount) {
    return micros(amount) < 0n;
}
/**
 * sumMoney adds a list of amounts that are all in the SAME currency.
 *
 * It adds micros as BigInt for the reason at the top of this file — a page of
 * usage records overruns 2^53 long before a balance does — and it THROWS on a
 * mixed-currency list rather than picking one currency and returning a number
 * that looks right. Credit cannot move between currencies, so their sum is not
 * a quantity anyone can spend; a caller holding several currencies wants a
 * total per currency (see `billing-common.tsx:sumByCurrency`), not this.
 *
 * An empty list has no currency to name, so it returns `undefined` rather than
 * inventing one. Callers render that as "—".
 */
export function sumMoney(amounts) {
    const first = amounts[0];
    if (!first) {
        return undefined;
    }
    let total = 0n;
    for (const amount of amounts) {
        if (amount.currency !== first.currency) {
            throw new Error(`sumMoney: cannot add ${amount.currency} to ${first.currency} — total one currency at a time`);
        }
        total += micros(amount);
    }
    return { currency: first.currency, micros: total.toString() };
}
/**
 * formatMoney renders an amount in MAJOR units — "USD 25.00" for the
 * 25,000,000 micros a $25 top-up is worth.
 *
 * It truncates toward zero at the requested precision rather than rounding.
 * A balance displayed as one cent MORE than the ledger will honour is a
 * customer being told they can afford a request that will be refused, and
 * that is a worse lie than being a cent conservative.
 *
 * `fractionDigits` is the currency's exponent (2 for USD/GBP/EUR, 0 for JPY)
 * — the wire carries no exponent, so the caller supplies it.
 */
export function formatMoney(amount, fractionDigits = 2) {
    const raw = micros(amount);
    const negative = raw < 0n;
    const magnitude = negative ? -raw : raw;
    // micros -> minor units is always a factor of MICROS_PER_MINOR; the decimal
    // point then goes fractionDigits from the right, which is what makes minor
    // units and major units the same number written differently.
    const minor = magnitude / MICROS_PER_MINOR;
    const digits = minor.toString().padStart(fractionDigits + 1, "0");
    const whole = digits.slice(0, digits.length - fractionDigits);
    const fraction = fractionDigits > 0 ? "." + digits.slice(digits.length - fractionDigits) : "";
    return `${negative ? "-" : ""}${amount.currency} ${withThousands(whole)}${fraction}`;
}
function withThousands(whole) {
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
//# sourceMappingURL=money.js.map